import { _ } from "../../../../../core/polyglot/polyglot.js";
import {
	circlePlusRegularIcon,
	percentIcon,
	trashAltIcon,
} from "../../../../../core/elements/icons/index.js";
import {
	iconAndText,
	spacer,
	stacker,
} from "../../../../../core/elements/index.js";
import AbstractWidgetsListStrategy from "../../../../../core/widgets/WidgetsListWidget/strategies/AbstractWidgetsListStrategy.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import FormBuilder from "../../../../../core/widgets/forms/FormBuilder.js";

const expectedTotalValue = 100;

/**
 * Strategy to render a payment spread.
 * It looks it bit like AccountPayableAndReceivableDelayStrategy, but not enough so
 * that reusing it would not be a spaghetti nightmare.
 *
 * @param {AbstractRuleModel} ruleModel - Rule holding the spread to edit
 */
export default class PaymentSpreadStrategy extends AbstractWidgetsListStrategy {
	constructor({ ruleModel }) {
		super(...arguments);

		this._ruleModel = ruleModel;
		this._itemToForm = new Map();
	}

	shouldExternalizeValidationRendering() {
		return true;
	}

	extraCSSClasses() {
		return "PaymentSpread";
	}

	canDeleteItem() {
		return this._getItemsOrEmpty().length > 1;
	}

	itemRenderer({ item, index }) {
		return spacer(
			{
				direction: "vertical",
				size: "small",
				fullWidth: true,
				align: "left",
			},
			this._makePaymentFieldLabel({ index }),
			this._makePaymentFieldInput({ item, index }),
		);
	}

	addButtonRenderer({ onClick }) {
		return button(
			{
				fullWidth: true,
				dataTest: "add-row",
				onClick,
			},
			iconAndText({ icon: circlePlusRegularIcon }, _("Add month")),
		);
	}

	deleteButtonRenderer({ onClick }) {
		return button(
			{
				onClick,
				dataTest: "delete",
			},
			trashAltIcon,
		);
	}

	addNewItem() {
		this._ruleModel.setPaymentSpread([
			...this._getItemsOrEmpty(),
			this._makeNewItem(),
		]);
	}

	deleteItem({ index }) {
		const updatedSpread = [...this._getItemsOrEmpty()];
		this._itemToForm.delete(updatedSpread[index]);
		updatedSpread.splice(index, 1);
		this._ruleModel.setPaymentSpread(updatedSpread);
	}

	_makeNewItem() {
		const totalValue = this._totalSpreadValue();

		if (totalValue < expectedTotalValue) {
			return expectedTotalValue - totalValue;
		}

		return "";
	}

	_getItemsOrEmpty() {
		return this._ruleModel.getPaymentSpread();
	}

	_makePaymentFieldLabel({ index }) {
		return index === 0
			? _("Current month")
			: _("Month {index}", { index: index + 1 });
	}

	_makePaymentFieldInput({ item, index }) {
		const form = this._makePaymentFieldForm({ item, index });

		this._itemToForm.set(item, form);

		return stacker(
			{ class: "payment-input", dataTest: "payment-input" },
			stacker({ fullWidth: true }, form),
			percentIcon,
		);
	}

	_makePaymentFieldForm({ item = "", index }) {
		const form = new FormBuilder({
			klass: "payment-field-form",
			vertical: true,
			showActions: false,
			showCancel: false,
		});

		form.input({
			name: `value-${index}`,
			type: "number",
			accessor: () => item,
			attributes: {
				change: ({ target: { value } }) => {
					const values = this._getItemsOrEmpty();

					this._updateItemToForm({
						item: values[index],
						newItem: +value,
					});

					values[index] = +value;
					this._ruleModel.setPaymentSpread(values);
				},
				min: "0",
			},
		});

		return form;
	}

	_validate({ collector }) {
		collector.addErrorIf(
			this._getItemsOrEmpty().some((value) => value === ""),
			_("No empty input is allowed"),
		);

		collector.addErrorIf(
			this._totalSpreadValue() !== expectedTotalValue,
			_("The payment spread total must equal {expectedTotalValue}", {
				expectedTotalValue,
			}),
		);
	}

	_totalSpreadValue() {
		return this._getItemsOrEmpty().reduce((result, value) => {
			if (value === "") {
				return result;
			}
			return result + value;
		}, 0);
	}

	_validateItem({ item, collector }) {
		const isValid = this._itemToForm.get(item)?.validate();
		collector.addErrorIf(!isValid, _("Invalid field"));
	}

	_updateItemToForm({ item, newItem }) {
		const form = this._itemToForm.get(item);
		this._itemToForm.delete(item);
		this._itemToForm.set(newItem, form);
	}
}
